import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  ArrowUpRight,
  Loader2,
  Check,
  Sparkles,
  ClipboardCheck,
} from "lucide-react";
import Seo from "../components/Seo";
import { businessSchema } from "../lib/seoSchemas";
import { CALENDLY_URL } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCALE = [
  { value: 1, label: "Not at all" },
  { value: 2, label: "Barely" },
  { value: 3, label: "Somewhat" },
  { value: 4, label: "Mostly" },
  { value: 5, label: "Fully in place" },
];

function ScaleSelector({ value, onChange, name }) {
  return (
    <div
      className="grid grid-cols-5 gap-2 sm:gap-3"
      role="radiogroup"
      aria-label={name}
      data-testid={`scale-${name}`}
    >
      {SCALE.map((s) => {
        const active = value === s.value;
        return (
          <button
            key={s.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(s.value)}
            className={`relative flex flex-col items-center justify-center gap-1 px-2 py-4 sm:py-5 border transition-colors ${
              active
                ? "bg-brand-700 border-brand-700 text-white"
                : "bg-white border-ink-line text-ink-soft hover:border-brand-400 hover:bg-brand-50/40"
            }`}
            data-testid={`scale-${name}-${s.value}`}
          >
            <span className="font-serif text-2xl leading-none">{s.value}</span>
            <span
              className={`text-[10px] tracking-[0.15em] uppercase text-center leading-tight ${
                active ? "text-brand-100" : "text-ink-muted"
              }`}
            >
              {s.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// -------------------- Step 1: Lead capture --------------------
function LeadStep({ roles, onStarted }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    locations: 1,
    role: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.role) {
      toast.error("Please select your role.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/assessment/start`, {
        ...form,
        locations: Number(form.locations) || 1,
      });
      onStarted(res.data?.id, form);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Could not start the assessment. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} data-testid="assessment-lead-form" className="bg-white border border-ink-line">
      <div className="p-8 lg:p-10 border-b border-ink-line">
        <div className="eyebrow mb-3">Step 1 of 3</div>
        <h2 className="font-serif text-3xl sm:text-4xl text-brand-700 leading-tight">
          A few details before we start.
        </h2>
        <p className="mt-3 text-ink-soft">
          The assessment takes about five minutes. You will receive your full scorecard by
          email immediately after.
        </p>
      </div>
      <div className="p-8 lg:p-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">Name</label>
          <input
            type="text"
            required
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className="w-full border border-ink-line bg-white px-4 py-3 focus:outline-none focus:border-brand-700 transition-colors"
            data-testid="assessment-input-name"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">Email</label>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            className="w-full border border-ink-line bg-white px-4 py-3 focus:outline-none focus:border-brand-700 transition-colors"
            data-testid="assessment-input-email"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">Company</label>
          <input
            type="text"
            required
            value={form.company}
            onChange={(e) => update("company", e.target.value)}
            className="w-full border border-ink-line bg-white px-4 py-3 focus:outline-none focus:border-brand-700 transition-colors"
            data-testid="assessment-input-company"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">
            Number of locations
          </label>
          <input
            type="number"
            min={1}
            max={9999}
            required
            value={form.locations}
            onChange={(e) => update("locations", e.target.value)}
            className="w-full border border-ink-line bg-white px-4 py-3 focus:outline-none focus:border-brand-700 transition-colors"
            data-testid="assessment-input-locations"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">Primary role</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
            {roles.map((r) => {
              const active = form.role === r;
              return (
                <button
                  type="button"
                  key={r}
                  onClick={() => update("role", r)}
                  className={`px-3 py-3 text-sm text-left border transition-colors ${
                    active
                      ? "bg-brand-700 border-brand-700 text-white"
                      : "bg-white border-ink-line text-ink-soft hover:border-brand-400 hover:bg-brand-50/40"
                  }`}
                  data-testid={`assessment-role-${r.replace(/\W+/g, "-").toLowerCase()}`}
                >
                  {r}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="p-8 lg:p-10 border-t border-ink-line bg-brand-50/30 flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs text-ink-muted max-w-md">
          By starting, you agree to receive your results and occasional operator-perspective
          notes from Caleb and Jake. No spam, unsubscribe anytime.
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-60 text-white px-7 py-4 text-sm font-medium transition-colors"
          data-testid="assessment-start-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
          Begin assessment
        </button>
      </div>
    </form>
  );
}

// -------------------- Step 2: Questions --------------------
function QuestionsStep({ categories, onSubmit, submitting }) {
  // answers shape: [[5,4,...],[...],...]
  const [answers, setAnswers] = useState(() => categories.map((c) => c.questions.map(() => null)));
  const [step, setStep] = useState(0); // category index

  const totalQuestions = useMemo(
    () => categories.reduce((acc, c) => acc + c.questions.length, 0),
    [categories]
  );
  const answeredCount = useMemo(
    () => answers.reduce((acc, cat) => acc + cat.filter((v) => v !== null).length, 0),
    [answers]
  );
  const progressPct = Math.round((answeredCount / totalQuestions) * 100);

  const current = categories[step];
  const currentAnswers = answers[step];
  const currentComplete = currentAnswers.every((v) => v !== null);
  const isLast = step === categories.length - 1;

  const setAnswer = (qIdx, v) => {
    setAnswers((prev) => {
      const next = prev.map((cat) => [...cat]);
      next[step][qIdx] = v;
      return next;
    });
  };

  const goNext = () => {
    if (!currentComplete) {
      toast.error("Please answer every question before continuing.");
      return;
    }
    if (isLast) {
      onSubmit(answers);
    } else {
      setStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goBack = () => {
    if (step > 0) {
      setStep((s) => s - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div data-testid="assessment-questions">
      {/* Progress bar */}
      <div className="bg-white border border-ink-line p-5 lg:p-6 mb-6">
        <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-brand-500 mb-3">
          <span>Step 2 of 3 &middot; Category {step + 1} of {categories.length}</span>
          <span data-testid="assessment-progress-label">{answeredCount} of {totalQuestions} answered</span>
        </div>
        <div className="bg-brand-100 h-1.5 overflow-hidden">
          <div
            className="bg-brand-700 h-1.5 transition-all duration-300"
            style={{ width: `${progressPct}%` }}
            data-testid="assessment-progress-bar"
          />
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-1.5">
          {categories.map((c, i) => {
            const isComplete = answers[i].every((v) => v !== null);
            const isCurrent = i === step;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setStep(i)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border transition-colors ${
                  isCurrent
                    ? "border-brand-700 bg-brand-700 text-white"
                    : isComplete
                    ? "border-brand-200 bg-brand-100 text-brand-700"
                    : "border-ink-line bg-white text-ink-muted hover:border-brand-400"
                }`}
                data-testid={`assessment-cat-chip-${c.key}`}
              >
                {isComplete && !isCurrent && <Check className="w-3 h-3" />}
                {c.title}
              </button>
            );
          })}
        </div>
      </div>

      {/* Category questions */}
      <div className="bg-white border border-ink-line">
        <div className="p-8 lg:p-10 border-b border-ink-line">
          <div className="eyebrow mb-2">Category {step + 1}</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-brand-700 leading-tight">
            {current.title}
          </h2>
          <p className="mt-3 text-ink-soft text-sm">
            Rate each statement on a 1-5 scale. 1 means it is not true of your organization,
            5 means it is fully in place.
          </p>
        </div>
        <ol className="divide-y divide-ink-line" data-testid={`questions-${current.key}`}>
          {current.questions.map((q, i) => (
            <li key={i} className="p-6 lg:p-8" data-testid={`question-${current.key}-${i}`}>
              <div className="flex items-baseline gap-3 mb-5">
                <span className="font-serif text-brand-400 text-sm leading-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="text-base lg:text-lg text-ink leading-snug">{q}</p>
              </div>
              <ScaleSelector
                value={currentAnswers[i]}
                onChange={(v) => setAnswer(i, v)}
                name={`${current.key}-${i}`}
              />
            </li>
          ))}
        </ol>
        <div className="p-6 lg:p-8 border-t border-ink-line bg-brand-50/30 flex items-center justify-between">
          <button
            type="button"
            onClick={goBack}
            disabled={step === 0}
            className="inline-flex items-center gap-2 text-brand-700 px-4 py-2.5 text-sm font-medium hover:bg-brand-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="assessment-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-60 text-white px-6 py-3 text-sm font-medium transition-colors"
            data-testid="assessment-next-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isLast ? (
              <Sparkles className="w-4 h-4" />
            ) : (
              <ArrowRight className="w-4 h-4" />
            )}
            {isLast ? "See my results" : "Next category"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------- Step 3: Results --------------------
function ResultsStep({ results, lead }) {
  return (
    <div data-testid="assessment-results">
      <div className="bg-brand-700 text-white p-8 lg:p-12 text-center">
        <div className="eyebrow text-brand-300 mb-3">Step 3 of 3 &middot; Your Scorecard</div>
        <h2 className="font-serif text-3xl sm:text-4xl leading-tight">
          {lead.company} Operations Readiness
        </h2>
        <div className="mt-6 inline-flex items-baseline gap-1">
          <span
            className="font-serif text-7xl sm:text-8xl leading-none"
            data-testid="results-total-score"
          >
            {results.total50}
          </span>
          <span className="text-brand-300 text-2xl font-serif">/50</span>
        </div>
        <p className="mt-4 text-brand-100/90 max-w-xl mx-auto text-sm">
          {results.overall_label}
        </p>
      </div>

      <div className="bg-white border border-x border-b border-ink-line">
        <div className="p-8 lg:p-12">
          <div className="eyebrow mb-2">Category Breakdown</div>
          <h3 className="font-serif text-2xl sm:text-3xl text-brand-700 mb-8">
            Where your operation stands, category by category.
          </h3>
          <ul className="space-y-8">
            {results.categories.map((c) => {
              const pct = Math.max(2, (c.score10 / 10) * 100);
              return (
                <li key={c.key} data-testid={`results-cat-${c.key}`}>
                  <div className="flex items-baseline justify-between gap-3 mb-2">
                    <div>
                      <div className="font-serif text-xl text-brand-700">{c.title}</div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-brand-500 mt-1">
                        {c.label}
                      </div>
                    </div>
                    <div className="font-serif text-3xl text-brand-700">
                      {c.score10}
                      <span className="text-ink-muted text-base">/10</span>
                    </div>
                  </div>
                  <div className="bg-brand-100 h-2 overflow-hidden">
                    <div
                      className="bg-brand-700 h-2 transition-all duration-700"
                      style={{ width: `${pct}%` }}
                      data-testid={`results-bar-${c.key}`}
                    />
                  </div>
                  <p className="mt-3 text-ink-soft text-sm leading-relaxed">{c.interpretation}</p>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="bg-brand-100 p-8 lg:p-12 text-center border-t border-brand-200">
          <h3 className="font-serif text-3xl text-brand-700 max-w-2xl mx-auto leading-tight">
            Want to walk through your results with an operator who's built these systems before?
          </h3>
          <p className="mt-4 text-ink-soft max-w-xl mx-auto text-sm">
            Thirty minutes, no pitch deck. We will turn this scorecard into a specific plan
            for the next ninety days.
          </p>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-6 bg-brand-700 hover:bg-brand-800 text-white px-7 py-4 text-sm font-medium transition-colors"
            data-testid="results-book-call"
          >
            Book a Call <ArrowUpRight className="w-4 h-4" />
          </a>
          <div className="mt-6 text-xs text-ink-muted">
            A copy of these results has been sent to your inbox.
          </div>
        </div>
      </div>

      <div className="mt-8 text-center">
        <Link
          to="/services"
          className="inline-flex items-center gap-2 text-brand-700 text-sm font-medium border-b border-brand-700 pb-1 hover:border-brand-400"
          data-testid="results-view-services"
        >
          See how HHC services map to these categories
        </Link>
      </div>
    </div>
  );
}

// -------------------- Wrapper page --------------------
export default function Assessment() {
  const [phase, setPhase] = useState("loading"); // loading | lead | questions | results
  const [categories, setCategories] = useState([]);
  const [roles, setRoles] = useState([]);
  const [assessmentId, setAssessmentId] = useState(null);
  const [lead, setLead] = useState(null);
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/assessment/questions`)
      .then((res) => {
        if (cancelled) return;
        setCategories(res.data?.categories || []);
        setRoles(res.data?.roles || []);
        setPhase("lead");
      })
      .catch(() => {
        if (cancelled) return;
        setPhase("lead");
        toast.error("Could not load the assessment. Please refresh.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleStarted = (id, leadData) => {
    setAssessmentId(id);
    setLead(leadData);
    setPhase("questions");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmitAnswers = async (answers) => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/assessment/${assessmentId}/submit`, { answers });
      setResults(res.data);
      setPhase("results");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Could not submit. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-assessment">
      <Seo
        title="Free Restaurant Operations Assessment | Holder Hospitality Consulting"
        description="Take the free Restaurant Operations Readiness Assessment and get a scored report showing where your operational gaps are across financial visibility, consistency, people, growth readiness, and compliance."
        schemas={[businessSchema]}
      />

      <section className="bg-white py-16 lg:py-24 border-b border-ink-line" data-testid="assessment-hero">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-6">Free Assessment</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-700 leading-[1.05] tracking-tight">
            Restaurant Operations Readiness.
          </h1>
          <p className="mt-6 text-lg text-ink-soft max-w-2xl leading-relaxed">
            Twenty-five questions across five categories. You will see exactly where your
            operation is strong, where it is leaking margin, and where it will break under
            growth. About five minutes. Free.
          </p>
        </div>
      </section>

      <section className="bg-brand-50/30 py-12 lg:py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          {phase === "loading" && (
            <div className="py-20 flex items-center justify-center text-ink-muted">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading assessment
            </div>
          )}
          {phase === "lead" && <LeadStep roles={roles} onStarted={handleStarted} />}
          {phase === "questions" && (
            <QuestionsStep
              categories={categories}
              onSubmit={handleSubmitAnswers}
              submitting={submitting}
            />
          )}
          {phase === "results" && results && <ResultsStep results={results} lead={lead} />}
        </div>
      </section>
    </div>
  );
}
